import { CampaignType, Platform } from '@round/api';
import styles from './RosterFilters.module.css';
import { StylesConfig, ValueType, components } from 'react-select';
import { CheckboxOption, Select, SelectedCountValueContainerProps, SelectedCountValueContainer } from '@round/ui-kit';
import { campaignTypeOptions } from '../../../../../../utils/constants';
import { useInfluencerTags } from '../../../../../../hooks/useInfluencerTags';
import useNonNullContext from '../../../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../../../contexts/OptionsContext';
import { GenericDropdownOption } from '../../../../../../utils/utils.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelectAll } from '@round/ui-kit';
import { platformOptions as basePlatformOptions } from '../../../../../../utils/constants';
import { mapInfluencerTagsToOptions } from '../../../../../../utils/utits';
import DropdownButton from '../../../../../../ui/Button/DropdownButton/DropdownButton';
import MinMaxSlider from '../../../../../../ui/Slider/MinMaxSlider/MinMaxSlider';
import { formatNumberToKNotation } from '../../../../../../utils/valueFormatHelpers';
import CategoriesFilter from './CategoriesFilter/CategoriesFilter';
import {
    parseConditionsFromUrlString,
    stringifyConditionsToUrlString,
} from './CategoriesFilter/CategoriesFilter.helpers';

export type RosterFiltersObject = {
    campaign_type: CampaignType | undefined;
    platforms: string[] | undefined;
    currency: number | undefined;
    categories: number[] | undefined;
    locations: number[] | undefined;
    max_followers: number | undefined;
    min_followers: number | undefined;
    min_cost: number | undefined;
    max_cost: number | undefined;
    content_tags: string | undefined;
};

type RosterFiltersProps = {
    filters: RosterFiltersObject;
    isCrucialFiltersDisabled?: boolean;
    onFilterChange: (value: Partial<RosterFiltersObject>) => void;
};

const filterStyles: StylesConfig = {
    container: (base) => ({
        ...base,
        flex: 1,
        minWidth: '5rem',
        maxWidth: '10rem',
    }),
    control: (base) => ({ ...base, height: '100%', borderRadius: '0.75rem' }),
    valueContainer: (base) => ({
        ...base,
        WebkitLineClamp: 1,
        display: 'flex',
        whiteSpace: 'nowrap',
        fontSize: '0.75rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }),
    placeholder: (base) => ({
        ...base,
        lineHeight: '1rem',
    }),
    menu: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
};

const currencyFilterStyles: StylesConfig = {
    ...filterStyles,
    container: (base) => ({
        ...base,
        flex: 1,
        minWidth: '5rem',
        maxWidth: '5rem',
    }),
    menu: (base) => ({
        ...base,
        minWidth: '5rem',
    }),
};

const CountValueContainer = ({ selectProps, ...props }: SelectedCountValueContainerProps<any>) => {
    const showSearchInput = selectProps.menuIsOpen && selectProps.isSearchable;
    if (showSearchInput) {
        return <components.ValueContainer selectProps={selectProps} {...props} />;
    }
    return (
        <SelectedCountValueContainer {...props} selectProps={selectProps} label={selectProps.label} numberOnly>
            {props.children}
        </SelectedCountValueContainer>
    );
};

const calculateFollowersScaleValue = (value: number) => {
    const scaleChangeThresholdSliderValue = 50;
    if (value < scaleChangeThresholdSliderValue) {
        return value * 20000;
    }
    return 200000 * value - 9000000;
};

const calculateFollowersSliderValue = (value: number) => {
    const scaleChangeThresholdRawValue = 1000000;
    if (value < scaleChangeThresholdRawValue) {
        return value / 20000;
    }
    return (value + 9000000) / 200000;
};

const calculateCostScaleValue = (value: number) => {
    const scaleChangeThresholdSliderValue = 50;
    if (value === 0) {
        return 50;
    }
    if (value < scaleChangeThresholdSliderValue) {
        return value * 20;
    }
    return 200 * value - 9000;
};

const calculateCostSliderValue = (value: number) => {
    const scaleChangeThresholdRawValue = 1000;
    if (value <= 50) {
        return 0;
    }
    if (value < scaleChangeThresholdRawValue) {
        return value / 20;
    }
    return (value + 9000) / 200;
};

const maxFollowersLimit = 10000000;

const defaultMinFollowersSliderValue = 0;
const defaultMaxFollowersSliderValue = 95;

const maxCostLimit = 10000;

const defaultMinCost = 50;

const defaultMinCostSliderValue = 0;
const defaultMaxCostSliderValue = 95;

const RosterFilters = ({ filters, isCrucialFiltersDisabled, onFilterChange }: RosterFiltersProps) => {
    const { currencies } = useNonNullContext(OptionsContext);
    const { influencerTags } = useInfluencerTags();

    const currencyOptions: GenericDropdownOption<number>[] = currencies
        //we don't support COP influencer costs
        .filter((c) => c.name !== 'COP')
        .map((c) => ({ value: c.id, label: c.name }));
    const selectedCurrency = currencyOptions.find((c) => c.value === Number(filters.currency));
    const selectedCurrencySymbol = currencies.find((curr) => curr.id === selectedCurrency?.value)?.symbol;

    //removing Youtube from the platform options until Youtube is properly supported
    const platformOptions = useMemo(() => basePlatformOptions.filter((option) => option.value !== 'YOUTUBE'), []);

    const selectedPlatforms = useMemo(
        () => platformOptions.filter((option) => filters.platforms?.includes(option.value)),
        [filters.platforms, platformOptions]
    );

    const { props: platformFilterProps } = useSelectAll({
        value: selectedPlatforms,
        options: platformOptions,
        onChange: (values: ValueType<GenericDropdownOption<Platform>, true>) =>
            onFilterChange({ platforms: values?.map((o) => o.value) ?? undefined }),
    });

    const selectedCampaignType: ValueType<GenericDropdownOption<CampaignType>, false> | undefined = useMemo(
        () => campaignTypeOptions.find((opt) => opt.value === filters.campaign_type),
        [filters.campaign_type]
    );

    const { locationTagOptions } = useMemo(() => mapInfluencerTagsToOptions(influencerTags), [influencerTags]);

    const selectedLocationTags = useMemo(() => {
        const locationTagIds = filters.locations?.map((id) => id) ?? [];
        return locationTagOptions.filter((option) => locationTagIds.includes(option.value));
    }, [locationTagOptions, filters.locations]);

    const { props: locationFilterProps } = useSelectAll({
        value: selectedLocationTags,
        options: locationTagOptions,
        onChange: (values: ValueType<GenericDropdownOption<number>, true>) =>
            onFilterChange({ locations: values?.map((o) => o.value) ?? undefined }),
    });

    const [[minFollowersSliderValue, maxFollowersSliderValue], setFollowersSliderValues] = useState<[number, number]>([
        filters.min_followers ? calculateFollowersSliderValue(filters.min_followers) : defaultMinFollowersSliderValue,
        filters.max_followers ? calculateFollowersSliderValue(filters.max_followers) : defaultMaxFollowersSliderValue,
    ]);
    const minFollowersDisplayValue = useMemo(() => {
        const scaledValue = calculateFollowersScaleValue(minFollowersSliderValue);
        return `${formatNumberToKNotation(scaledValue)}`;
    }, [minFollowersSliderValue]);

    const maxFollowersDisplayValue = useMemo(() => {
        const scaledValue = calculateFollowersScaleValue(maxFollowersSliderValue);
        return scaledValue === maxFollowersLimit
            ? `${formatNumberToKNotation(scaledValue)}+`
            : formatNumberToKNotation(scaledValue);
    }, [maxFollowersSliderValue]);

    const displayedFollowerValues = [minFollowersDisplayValue, maxFollowersDisplayValue];

    const setFollowersfilters = useCallback(
        (values: [number, number]) => {
            const [min, max] = values;
            const scaledMinValue = calculateFollowersScaleValue(min);
            const scaledMaxValue = calculateFollowersScaleValue(max);
            onFilterChange({
                min_followers: scaledMinValue > 0 ? scaledMinValue : undefined,
                max_followers: scaledMaxValue !== maxFollowersLimit ? scaledMaxValue : undefined,
            });
        },
        [onFilterChange]
    );

    const [[minCostSliderValue, maxCostSliderValue], setCostSliderValues] = useState<[number, number]>([
        filters.min_cost ? calculateCostSliderValue(filters.min_cost) : defaultMinCostSliderValue,
        filters.max_cost ? calculateCostSliderValue(filters.max_cost) : defaultMaxCostSliderValue,
    ]);
    const minCostDisplayValue = useMemo(() => {
        const scaledValue = calculateCostScaleValue(minCostSliderValue);
        return `${selectedCurrencySymbol}${formatNumberToKNotation(scaledValue)}`;
    }, [minCostSliderValue, selectedCurrencySymbol]);

    const maxCostDisplayValue = useMemo(() => {
        const scaledValue = calculateCostScaleValue(maxCostSliderValue);
        return maxCostSliderValue === maxCostLimit
            ? `${selectedCurrencySymbol}${formatNumberToKNotation(scaledValue)}+`
            : `${selectedCurrencySymbol}${formatNumberToKNotation(scaledValue)}`;
    }, [maxCostSliderValue, selectedCurrencySymbol]);

    const displayedCostValues = [minCostDisplayValue, maxCostDisplayValue];

    const setCostfilters = useCallback(
        (values: [number, number]) => {
            const [min, max] = values;
            const scaledMinValue = calculateCostScaleValue(min);
            const scaledMaxValue = calculateCostScaleValue(max);
            onFilterChange({
                min_cost: scaledMinValue > defaultMinCost ? scaledMinValue : defaultMinCost,
                max_cost: scaledMaxValue !== maxCostLimit ? scaledMaxValue : undefined,
            });
        },
        [onFilterChange]
    );

    useEffect(() => {
        if (!selectedCurrency && currencyOptions?.length) {
            onFilterChange({ currency: currencyOptions.find((c) => c.label === 'USD')?.value });
        }
        if (!selectedCampaignType) {
            onFilterChange({ campaign_type: campaignTypeOptions?.find((opt) => opt.value === 'AUDIO')?.value });
        }
    }, [selectedCurrency, selectedCampaignType, currencyOptions, onFilterChange]);

    return (
        <>
            <div className={styles.filters}>
                <Select
                    isSearchable={false}
                    styles={filterStyles}
                    value={selectedCampaignType}
                    onChange={(option) =>
                        onFilterChange({
                            campaign_type: (option as ValueType<GenericDropdownOption<CampaignType>, false>)?.value,
                        })
                    }
                    options={campaignTypeOptions}
                    components={{ Option: CheckboxOption }}
                    isDisabled={isCrucialFiltersDisabled}
                />
                <Select
                    isSearchable={false}
                    styles={currencyFilterStyles}
                    value={selectedCurrency}
                    onChange={(option) =>
                        onFilterChange({ currency: (option as ValueType<GenericDropdownOption<number>, false>)?.value })
                    }
                    options={currencyOptions}
                    components={{ Option: CheckboxOption }}
                    isDisabled={isCrucialFiltersDisabled}
                />
                <Select
                    isSearchable={false}
                    isClearable={false}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    styles={filterStyles}
                    placeholder="Select platforms"
                    label="Platforms: "
                    {...(platformFilterProps as any)}
                    components={{ Option: CheckboxOption, ValueContainer: CountValueContainer as any }}
                />
                <CategoriesFilter
                    conditions={parseConditionsFromUrlString(filters.content_tags)}
                    onChange={(conditions) => {
                        onFilterChange({ content_tags: stringifyConditionsToUrlString(conditions) });
                    }}
                />
                <Select
                    {...(locationFilterProps as any)}
                    styles={filterStyles}
                    isClearable={false}
                    hideSelectedOptions={false}
                    controlShouldRenderValue={false}
                    closeMenuOnSelect={false}
                    placeholder="Select locations"
                    label="Locations: "
                    components={{ Option: CheckboxOption, ValueContainer: CountValueContainer as any }}
                />
                <div className={styles.filterColumn}>
                    <span className={styles.filterTitle}>Followers</span>
                    <DropdownButton
                        label={`${minFollowersDisplayValue} - ${maxFollowersDisplayValue}`}
                        className={styles.sliderButton}
                        popoverOptions={{
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 1],
                                    },
                                },
                            ],
                        }}
                    >
                        <div className={styles.sliderContainer}>
                            <MinMaxSlider
                                className={styles.dropdownSlider}
                                stepSize={5}
                                values={[minFollowersSliderValue, maxFollowersSliderValue]}
                                max={defaultMaxFollowersSliderValue}
                                onChange={(values) => {
                                    setFollowersSliderValues(values);
                                }}
                                onAfterChange={(values) => {
                                    setFollowersfilters(values);
                                }}
                                minDistance={10}
                                renderThumb={(props, state) => (
                                    <div {...props} className={styles.thumb}>
                                        <div className={styles.thumbValue}>{displayedFollowerValues[state.index]}</div>
                                    </div>
                                )}
                            />
                        </div>
                    </DropdownButton>
                </div>
                <div className={styles.filterColumn}>
                    <span className={styles.filterTitle}>Price</span>
                    <DropdownButton
                        label={`${minCostDisplayValue} - ${maxCostDisplayValue}`}
                        className={styles.sliderButton}
                        popoverOptions={{
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 1],
                                    },
                                },
                            ],
                        }}
                    >
                        <div className={styles.sliderContainer}>
                            <MinMaxSlider
                                className={styles.dropdownSlider}
                                stepSize={5}
                                values={[minCostSliderValue, maxCostSliderValue]}
                                min={defaultMinCostSliderValue}
                                max={defaultMaxCostSliderValue}
                                onChange={(values) => {
                                    setCostSliderValues(values);
                                }}
                                onAfterChange={(values) => {
                                    setCostfilters(values);
                                }}
                                renderThumb={(props, state) => (
                                    <div {...props} className={styles.thumb}>
                                        <div className={styles.thumbValue}>{displayedCostValues[state.index]}</div>
                                    </div>
                                )}
                                minDistance={10}
                            />
                        </div>
                    </DropdownButton>
                </div>
            </div>
        </>
    );
};

export default RosterFilters;
